import { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { CircleAlert, Info, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { client } from '../../services/api';
import {
  contractCurrencyFormatter,
  globalContractColumns,
  globalContractPageSize,
  globalContractSearchDelay,
} from '../../utils/contractsData';
import { successToastConfig } from '../../utils/data';
import { InputTextField } from '../Input/InputTextField';
import { LoaderSpinner } from '../Layout/LoaderSpinner';
import {
  PaginationButton,
  PaginationControls,
  PaginationInfo,
} from '../Styled/table';
import ApplyGlobalContractModal from './ApplyGlobalContractModal';

const getGlobalContractId = (contract) => {
  return contract.id || contract._id || [
    contract.practiceName,
    contract.providerName,
    contract.code,
    contract.grossBundle,
  ].join('|');
};

const normalizeGlobalContract = (contract) => {
  const normalizedContract = globalContractColumns.reduce((result, column) => ({
    ...result,
    [column.key]: contract[column.key] ?? contract[column.apiKey] ?? '',
  }), {});

  return {
    ...normalizedContract,
    id: contract.id ?? contract._id,
  };
};

const getContractsFromResponse = (response) => {
  const responseData = response?.data || {};
  const contracts = Array.isArray(responseData.contracts) ? responseData.contracts : [];
  const parsedTotalRecords = Number(responseData.totalRecords);

  return {
    contracts: contracts.map(normalizeGlobalContract),
    totalRecords: Number.isFinite(parsedTotalRecords)
      ? parsedTotalRecords
      : contracts.length,
  };
};

const getContractCellValue = (contract, column) => {
  const value = contract[column.key];

  if (column.key !== 'grossBundle' || value === null || value === undefined || value === '') {
    return value ?? '';
  }

  const numericValue = Number(String(value).replace(/[$,]/g, ''));
  return Number.isNaN(numericValue) ? value : contractCurrencyFormatter.format(numericValue);
};

const GlobalContracts = ({ onApplyContract }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [globalContracts, setGlobalContracts] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState('');

  const normalizedSearch = searchTerm.trim();
  const selectedContract = globalContracts.find(
    (contract) => getGlobalContractId(contract) === selectedContractId
  );
  const pageStart = totalRecords === 0 ? 0 : (offset * globalContractPageSize) + 1;
  const pageEnd = Math.min(
    (offset * globalContractPageSize) + globalContracts.length,
    totalRecords
  );
  const canPreviousPage = offset > 0;
  const canNextPage = pageEnd < totalRecords;

  useEffect(() => {
    if (!normalizedSearch) {
      setDebouncedSearch('');
      setGlobalContracts([]);
      setOffset(0);
      setTotalRecords(0);
      setSelectedContractId(null);
      setIsLoading(false);
      setHasSearched(false);
      setSearchError('');
      return undefined;
    }

    setDebouncedSearch('');
    setGlobalContracts([]);
    setOffset(0);
    setTotalRecords(0);
    setSelectedContractId(null);
    setIsLoading(false);
    setHasSearched(false);
    setSearchError('');

    const searchTimer = setTimeout(() => {
      setDebouncedSearch(normalizedSearch);
    }, globalContractSearchDelay);

    return () => clearTimeout(searchTimer);
  }, [normalizedSearch]);

  useEffect(() => {
    if (!debouncedSearch) {
      return undefined;
    }

    let isCurrentRequest = true;
    setGlobalContracts([]);
    setSelectedContractId(null);
    setIsLoading(true);
    setSearchError('');

    const fetchContracts = async () => {
      try {
        const response = await client.getGlobalContracts({
          prefix: debouncedSearch,
          offset,
        });

        if (!isCurrentRequest) {
          return;
        }

        if (!response?.ok) {
          setSearchError("We couldn't retrieve global contracts. Please try again.");
          setTotalRecords(0);
          return;
        }

        const result = getContractsFromResponse(response);
        setGlobalContracts(result.contracts);
        setTotalRecords(result.totalRecords);
      } catch {
        if (isCurrentRequest) {
          setSearchError("We couldn't retrieve global contracts. Please try again.");
          setGlobalContracts([]);
          setTotalRecords(0);
        }
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
          setHasSearched(true);
        }
      }
    };

    fetchContracts();

    return () => {
      isCurrentRequest = false;
    };
  }, [debouncedSearch, offset]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleApplyContract = () => {
    if (selectedContract) {
      setShowApplyModal(true);
    }
  };

  const handleSelectContract = (contract) => {
    setSelectedContractId(getGlobalContractId(contract));
  };

  const handlePreviousPage = () => {
    setOffset((currentOffset) => Math.max(currentOffset - 1, 0));
  };

  const handleNextPage = () => {
    setOffset((currentOffset) => currentOffset + 1);
  };

  const handleAgreeApply = (applicationDetails) => {
    if (!selectedContract) {
      return;
    }

    onApplyContract?.(selectedContract, applicationDetails);
    setSelectedContractId(null);
    setShowApplyModal(false);
    toast('Global contract applied successfully', successToastConfig);
  };

  return (
    <>
      <section className="global-contract-search" aria-label="Search global contracts">
        <div className="global-contract-search__content">
          <h2 className="global-contract-search__title">Search Global Contracts</h2>

          <InputTextField
            onChange={handleSearchChange}
            placeholder="Enter a practice, provider, location, code, or rate"
            value={searchTerm}
            endIcon={<Search size={16} />}
            groupStyle={{ margin: 0 }}
          />

          <div className="global-contract-search__guide">
            <Info size={18} aria-hidden="true" />
            <div>
              <p className="global-contract-search__guide-label">
                Search using any contract detail
              </p>
              <p className="global-contract-search__guide-text">
                Practice name, provider name, office address, city, state, ZIP,
                code type, code, description, or gross bundle.
              </p>
            </div>
          </div>

          {isLoading && (
            <div className="global-contract-search__loading">
              <LoaderSpinner
                absolute={false}
                style={{ width: 20, height: 20, marginRight: 10 }}
              />
              <span>Searching global contracts...</span>
            </div>
          )}

          {!isLoading && searchError && (
            <div className="global-contract-search__status global-contract-search__status--error" role="alert">
              <CircleAlert size={19} aria-hidden="true" />
              <div>
                <p className="global-contract-search__status-title">Search unavailable</p>
                <p className="global-contract-search__status-text">{searchError}</p>
              </div>
            </div>
          )}

          {!isLoading && hasSearched && !searchError && globalContracts.length === 0 && (
            <div className="global-contract-search__status" role="status">
              <Search size={19} aria-hidden="true" />
              <div>
                <p className="global-contract-search__status-title">No matches found</p>
                <p className="global-contract-search__status-text">
                  Try another practice, provider, location, code, or rate.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {!isLoading && globalContracts.length > 0 && (
        <div className="data-feed-panel contract-panel--table contract-panel--selectable mt-3" role="tabpanel">
          <div className="contract-panel__header">
            <h3 className="contract-panel__title">Matching Global Contracts</h3>
            <div className="contract-panel__header-actions">
              <PaginationInfo>
                Showing {pageStart} to {pageEnd} of {totalRecords} results
              </PaginationInfo>
              <PaginationControls>
                <PaginationButton
                  onClick={handlePreviousPage}
                  disabled={!canPreviousPage}
                >
                  Previous
                </PaginationButton>
                <PaginationButton
                  onClick={handleNextPage}
                  disabled={!canNextPage}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleApplyContract}
                disabled={!selectedContract}
              >
                Apply Contract
              </Button>
            </div>
          </div>

          <div className="data-feed-table-wrapper contract-table-wrapper">
            <table className="data-feed-table contract-table contract-table--global">
              <thead>
                <tr className="data-feed-table__header-row">
                  <th className="data-feed-table__header-cell contract-table__header-cell contract-table__select-cell" aria-label="Select contract" />
                  {globalContractColumns.map((column) => (
                    <th key={column.key} className="data-feed-table__header-cell contract-table__header-cell">
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {globalContracts.map((contract, index) => {
                  const contractId = getGlobalContractId(contract);
                  const isSelected = contractId === selectedContractId;

                  return (
                    <tr
                      key={contractId || index}
                      className={isSelected ? 'contract-table__row--selected' : ''}
                      onClick={() => handleSelectContract(contract)}
                    >
                      <td className="data-feed-table__cell contract-table__cell contract-table__select-cell">
                        <span
                          className={`contract-table__radio${isSelected ? ' contract-table__radio--selected' : ''}`}
                          aria-hidden="true"
                        />
                      </td>
                      {globalContractColumns.map((column) => (
                        <td key={column.key} className="data-feed-table__cell contract-table__cell">
                          {getContractCellValue(contract, column)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showApplyModal && (
        <ApplyGlobalContractModal
          onAgree={handleAgreeApply}
          onClose={() => setShowApplyModal(false)}
        />
      )}
    </>
  );
};

export default GlobalContracts;
